<template>
  <div class="year-month-selector" @click.prevent.stop="">
    <div class="year-month-controls">
      <button
        type="button"
        tabindex="0"
        class="arrow-button arrow-prev"
        :class="{'button-disable': isPrevDisabled }"
        :disabled="isPrevDisabled"
        @click="changeYearMonth('prev')"
      >
        <img alt="close" class="lazyload" src="../assets/icons/arrow-left.svg">
      </button>

      <YearMonthButton
        color="#ffffff"
        transparent
        @click="isMonthMode ? $emit('loadMode', 'year') : $emit('loadMode', 'month')"
      >
        <span class="no-translate month-name">
          {{ isMonthMode ? month.year : month.getFormatted() }}
        </span>
      </YearMonthButton>

      <button
        type="button"
        tabindex="0"
        class="arrow-button arrow-next"
        @click="changeYearMonth('next')"
      >
        <img alt="close" class="lazyload" src="../assets/icons/arrow-right.svg">
      </button>
    </div>
    <div class="year-month-buttons no-translate">
      <YearMonthButton
        v-for="(m, index) in months"
        :key="index"
        class="month-button"
        color="#ffffff"
        :selected="currentMonth === index"
        transparent
        :disabled="isMonthDisabled(index)"
        @click="selectMonth(index)"
      >
        {{ m }}
      </YearMonthButton>
      <YearMonthButton
        v-for="year in years"
        :key="year"
        color="#ffffff"
        :selected="currentYear === year"
        transparent
        :disabled="isYearDisabled(year)"
        @click="selectYear(year)"
      >
        {{ year }}
      </YearMonthButton>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import YearMonthButton from './YearMonthButton';
import { getMonthsShort } from '../modules/month';

const ArrayRange = (start, end) => Array(end - start + 1).fill().map((_, idx) => {
  const n = start + idx;
  return n;
});

export default {
  name: 'YearMonthSelector',
  components: {
    YearMonthButton,
  },
  props: {
    locale: { type: String, default: null },
    mode: { type: String, default: null },
    month: { type: Object, default: null },
    disablePastDates: { type: Boolean, default: null },
  },
  data() {
    return {
      months: null,
      years: null,
    };
  },
  computed: {
    currentMonth() {
      return this.month.month;
    },
    currentYear() {
      return this.month.year;
    },
    isMonthMode() {
      return this.mode === 'month';
    },
    isPrevDisabled() {
      const previousMonthIndex = moment(this.month.start).subtract(1, 'months').month();
      return this.disablePastDates
        ? this.isMonthMode ? this.isYearDisabled(this.month.year - 1) : this.isMonthDisabled(previousMonthIndex)
        : false;
    },
  },
  mounted() {
    if (this.isMonthMode) {
      this.getMonths();
    } else {
      this.getYears();
    }
  },
  methods: {
    isMonthDisabled(index) {
      return this.disablePastDates ? ((moment().month(index)).isBefore(moment()) && this.month.year <= moment().year()) || (moment().year(this.month.year)).isBefore(moment()) : false;
    },
    isYearDisabled(year) {
      return this.disablePastDates ? (moment().year(year)).isBefore(moment()) : false;
    },
    getMonths() {
      this.years = null;
      this.months = getMonthsShort(this.locale);
    },
    getYears() {
      this.months = null;
      this.years = ArrayRange(this.month.year - 7, this.month.year + 7);
    },
    selectMonth(monthNumber) {
      this.$emit('input', { month: monthNumber, year: this.currentYear });
    },
    selectYear(year) {
      this.$emit('input', { month: this.currentMonth, year });
    },
    changeYearMonth(dir) {
      if (this.isMonthMode) {
        this.$emit('changeYear', { month: this.currentMonth, year: this.month.year, dir });
      } else {
        this.$emit('changeMonth', dir);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/datepicker.scss";

.year-month-selector{
  position: absolute;
  display: flex;
  flex-direction: column;
  font-family: $font-family-grotesk;
  background-color: $stoneDark;
  top: 55px;
  bottom: 0;
  left: 0;
  right: 0;
  color: $darkBlack;
  padding: 10px;
  width: 250px;
  height: 250px;
  left: 50%;
  transform: translate(-50%);
  z-index: 99999;

  @include dp-mobile-down-sm(){
    width: 88%;
    height: 230px;
    top: 30px;
  }

  &:before{
    content:'';
    display:block;
    width:0;
    height:0;
    position:absolute;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom:8px solid $stoneDark;
    top:-8px;
    left: 50%;
    transform: translate(-50%);
  }

  .month-button {
    text-transform: uppercase;
  }
}

.year-month-controls{
  display: flex;
  justify-content: center;

  .arrow-button {
    background: transparent;
    cursor: pointer;
    padding: 0 10px;
    border: none;
    outline: none;
    display: flex;
    text-align: center;
    align-items: center;
    img {
      height: 17px;
      width: 17px;
      fill: $stoneWhite;
    }
    img:hover{
      fill: $stoneCream;
    }
    &.arrow-prev {
      text-align: left !important;
      //transform: translateY(0%) rotate(180deg);
    }
    &.arrow-next {
      text-align: right !important;
    }

    &.button-disable{
      opacity: 0.5;
      cursor: auto;
      pointer-events: none;
    }
  }
}

.year-month-buttons{
  display: flex;
  flex-direction: row;
  flex: 1;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  @include dp-mobile-down-sm (){
    margin:0 20px 0 10px; //10px extra on the right for the scroll bar
  }
}

.month-name{
  text-transform: uppercase;
}

</style>
